/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React from 'react';
import { connect } from 'react-redux';

import type { Noop } from '../../../../ts';
import type { Account } from '../../../../ts/account';
import type { Assistant, Environment } from '../../../../ts/assistant';
import type { CatalogItem } from '../../../../ts/catalog';
import type { ChannelInstance } from '../../../../ts/channel';
import type { PlanMetering, PlanName } from '../../../../ts/plan';
import type { BluemixAnalytics } from '../../../../ts/segment';
import type { Skill, TopicSwitch } from '../../../../ts/skill';
import type { RootState, SessionSlice } from '../../../../ts/state';
import {
  getAllActions,
  getCurrentAction,
  getCurrentStep,
  getCurrentStepOptions,
  getOrderedSteps,
  getCurrentStepValidation,
  getEndStepTypeCount,
  getTriggerExamples,
  isAutoSaveEnabled,
  getAllSteps
} from '../../../actions/store/actions/selectors';
import { selectAllIntegrations } from '../../../channels/store/channels/selectors';
import {
  getInstanceRole,
  getAssistantRole,
  getSkillRole
} from '../../store/access-control/selectors';
import { selectAssistant } from '../../store/assistants/core-selectors';
import {
  selectAllEnvironments,
  selectDraftEnvironment
} from '../../store/environments/selectors';
import {
  selectPlan,
  selectPlanMetering,
  selectIsNewIa
} from '../../store/instance/selectors';
import { PLAN_NAMES } from '../../store/plan/constants';
import {
  selectInstanceRegion,
  selectServiceInstanceId
} from '../../store/session/selectors';
import { selectSkill } from '../../store/skills/core-selectors';
import { selectTopicSwitchSettings } from '../../store/skills/selectors';
import { hasContactModule, setFocusArea } from '../../util/contactModule';
import { setUtilQueueHandlers } from '../../util/trackers/segment/segmentUtil';
import SegmentEvent, { eventTypes } from './SegmentEvent';
import {
  validateEventProps,
  validateCustomProps,
  getProductProps,
  CUSTOM_ENVIRONMENT_ID,
  CUSTOM_EXTENSION_ID,
  CUSTOM_WA_INTEGRATION_ID,
  CUSTOM_WA_ENVIRONMENT_ID,
  CUSTOM_TARGET_ENVIRONMENT_ID,
  CUSTOM_WA_TARGET_ENVIRONMENT_ID
} from './segmentProperties';

export interface SegmentTrackerProps {
  account: Account;
  accountType: string;
  actionDetails: {
    numEndSteps: number;
    numReAskSteps: number;
    numReturnSteps: number;
    totalSteps: number;
  };
  actionsAllActions: any;
  actionsAllSteps: any;
  actionsAutoSaveEnabled: boolean;
  actionsCurrentAction: any;
  actionsStepData: any;
  actionsTriggerExamples: any;
  assistant: Assistant;
  customExtensions: CatalogItem[];
  draftEnvironment: Environment;
  environments: Environment[];
  integrations: ChannelInstance[];
  isNewIa: boolean;
  plan: PlanName;
  metering?: PlanMetering;
  region: string;
  roles: { instance: string; assistant: string; skill: string };
  serviceInstanceId: string;
  session: SessionSlice;
  skill: Skill;
  topicSwitchSettings: TopicSwitch;
}

interface SegmentTrackerState {
  cmApp?: {
    refresh: Noop;
  };
  bluemixAnalytics?: BluemixAnalytics;
  eventQueue?: any[];
}

class SegmentTracker extends React.Component<
  SegmentTrackerProps,
  SegmentTrackerState
> {
  constructor(props) {
    super(props);
    this.state = {
      bluemixAnalytics: null,
      cmApp: null,
      eventQueue: []
    };
  }

  componentDidMount() {
    setUtilQueueHandlers({
      trackEvent: this.queueTrackEvent,
      pageEvent: this.queuePageEvent
    });
    this.awaitBluemixAnalytics();
    this.awaitCmApp();
  }

  componentDidUpdate() {
    this.initIbmMeta();
    this.initCMaaS();
  }

  initIbmMeta() {
    const { plan, session, accountType } = this.props;

    if (session && session.user && session.user.name && !window.IBM_Meta) {
      const names = session.user.name.split(' ');
      const product = getProductProps();
      //bluemix-analytics script adds to this object
      window.IBM_Meta = {
        offeringId: product.productId,
        offeringName: 'IBM Watson Assistant for IBM Cloud',
        userEmail: session.user.email,
        customerName: session.user.name,
        userFirstName: names[0],
        userLastName: names[names.length - 1],
        triggerIntercept: 'light',
        trigger1: false,
        instancePlan: plan,
        testData: false,
        country: 'US',
        language: 'en'
      };
    }
    // Needed for Medallia
    if (window.IBM_Meta && accountType && plan) {
      const isTrialUser =
        (plan === PLAN_NAMES.LITE || plan === PLAN_NAMES.PLUS_TRIAL) &&
        (accountType === 'STANDARD' ||
          accountType === 'FREE' ||
          accountType === 'TRIAL')
          ? 'yes'
          : 'no';
      if (window.IBM_Meta.trialUser !== isTrialUser) {
        window.IBM_Meta.trialUser = isTrialUser;
      }
    }
    // Need to initialize IBM_Meta above asap - can't wait for limits call.
    // Added below check to change instancePlan once limits updates
    if (window.IBM_Meta && window.IBM_Meta.instancePlan !== plan) {
      window.IBM_Meta.instancePlan = plan;
    }
  }

  initCMaaS() {
    const { cmApp } = this.state;
    if (cmApp && !hasContactModule()) {
      setFocusArea('CDP - DataAI - Watson Assistant CSM');
      cmApp.refresh();
    }
  }

  render() {
    const { bluemixAnalytics, eventQueue } = this.state;
    if (bluemixAnalytics) {
      while (eventQueue.length) {
        const event = eventQueue.pop();
        try {
          switch (event.type) {
            case eventTypes.trackEvent:
              bluemixAnalytics.trackEvent(event.name, event.data);
              break;
            case eventTypes.pageEvent:
              bluemixAnalytics.pageEvent(event.name, event.data);
              bluemixAnalytics.identify(event.identifyData);
              break;
            default:
              break;
          }
        } catch (error) {
          console.error('Error sending event to segment', error);
        }
      }
    }
    return null;
  }

  queuePageEvent = (name, supportedData, eventData, customData, groupProps) => {
    const data = {
      ...validateEventProps(eventData, supportedData),
      ...validateCustomProps(customData)
    };
    this.queueEvent(eventTypes.pageEvent, name, data, groupProps);
  };

  queueTrackEvent = (
    name,
    supportedData,
    eventData,
    customData,
    groupProps
  ) => {
    const data = {
      ...validateEventProps(eventData, supportedData),
      ...validateCustomProps(customData)
    };
    this.queueEvent(eventTypes.trackEvent, name, data, groupProps);
  };

  queueEvent = (type, name, data, groupProps) => {
    const {
      account,
      actionDetails,
      actionsAllSteps,
      actionsAutoSaveEnabled,
      actionsCurrentAction,
      actionsStepData,
      actionsTriggerExamples,
      assistant,
      customExtensions,
      draftEnvironment,
      environments,
      integrations,
      isNewIa,
      metering,
      plan,
      region,
      roles,
      serviceInstanceId,
      skill,
      topicSwitchSettings
    } = this.props;

    const event = new SegmentEvent(type, name, data);
    // Add New IA flag for all events
    event.addNewIaFlag(isNewIa);

    // Add Assistant data for all new IA events
    let assistantTracked = false;
    if (isNewIa && assistant && roles) {
      event.addAssistantData(assistant, roles);
      assistantTracked = true;
    }

    if (type === eventTypes.pageEvent) {
      event.addIdentifyData(plan);
    }
    if (groupProps) {
      if (groupProps.common) {
        event.addAccountData(account);
        event.addInstanceData(serviceInstanceId, region, plan, metering);
        event.addAccessControlData(roles);
      }

      if (groupProps.assistant && !assistantTracked) {
        event.addAssistantData(assistant, roles);
      }

      if (groupProps.skill) {
        event.addSkillData(skill, roles);
      }

      if (groupProps.integration) {
        const intId = data[CUSTOM_WA_INTEGRATION_ID];
        if (intId) {
          const integration = integrations?.find(e => e.id === intId);
          event.addIntegrationData(integration);
        } else {
          console.warn(
            `Missing "${CUSTOM_WA_INTEGRATION_ID}" from segment event`
          );
        }
      }

      if (groupProps.customExtension) {
        const extensionId = data[CUSTOM_EXTENSION_ID];
        if (extensionId) {
          const customExtension = customExtensions?.find(
            e => e.catalog_item_id === extensionId
          );
          event.addCustomExtensionData(customExtension);
        } else {
          console.warn(`Missing "${CUSTOM_EXTENSION_ID}" from segment event`);
        }
      }

      if (groupProps.action) {
        const confirmation_enabled =
          topicSwitchSettings?.messages?.enable_confirmation;
        event.addActionData(
          actionsCurrentAction,
          actionDetails,
          actionsTriggerExamples,
          actionsAutoSaveEnabled,
          actionsAllSteps,
          assistant,
          confirmation_enabled
        );
      }
      if (groupProps.step) {
        event.addStepData(actionsStepData);
      }

      if (isNewIa && groupProps.environment) {
        // Environment data is calculated at runtime
        let environmentId =
          data[CUSTOM_ENVIRONMENT_ID] || data[CUSTOM_WA_ENVIRONMENT_ID];

        // If the trace point requires environment data and none was provided,
        // assume this is happening in the context of the Draft environment
        if (!environmentId && draftEnvironment) {
          environmentId = draftEnvironment.environment_id;
        }

        if (environmentId) {
          const environment = environments?.find(
            e => e.environment_id === environmentId
          );
          event.addEnvironmentData(environment);
        }

        const targetEnvironmentId =
          data[CUSTOM_TARGET_ENVIRONMENT_ID] ||
          data[CUSTOM_WA_TARGET_ENVIRONMENT_ID];

        if (targetEnvironmentId) {
          const environment = environments?.find(
            e => e.environment_id === targetEnvironmentId
          );
          event.addTargetEnvironmentData(environment);
        }
      }
    }

    this.setState(state => ({ eventQueue: [event, ...state.eventQueue] }));
  };

  awaitBluemixAnalytics = () => {
    if (window.bluemixAnalytics) {
      this.setState({ bluemixAnalytics: window.bluemixAnalytics });
    } else {
      setTimeout(this.awaitBluemixAnalytics, 500);
    }
  };

  awaitCmApp = () => {
    if (window.CM_APP) {
      this.setState({ cmApp: window.CM_APP });
    } else {
      setTimeout(this.awaitCmApp, 500);
    }
  };
}

const mapStateToProps = (state: RootState) => {
  const account = state.session.account;
  const isNewIa = selectIsNewIa(state);
  const accountType = account?.type;
  const serviceInstanceId = selectServiceInstanceId(state);
  const region = selectInstanceRegion(state);
  const assistant = selectAssistant(state);
  const skill = selectSkill(state);
  const actionsAllActions = getAllActions(state);
  const actionsCurrentAction = getCurrentAction(state);
  const actionsStep = getCurrentStep(state);
  const actionsStepOptions = getCurrentStepOptions(state);
  const actionsOrderedSteps = getOrderedSteps(state);
  const actionsStepValidation = getCurrentStepValidation(state);
  const actionsStepData = {
    actionsStep,
    actionsStepOptions,
    actionsOrderedSteps,
    actionsStepValidation,
    assistant
  };
  const actionDetails = getEndStepTypeCount(state);
  const actionsTriggerExamples = getTriggerExamples(state);
  const actionsAutoSaveEnabled = isAutoSaveEnabled(state);
  const actionsAllSteps = getAllSteps(state);
  const environments = selectAllEnvironments(state);
  const draftEnvironment = selectDraftEnvironment(state);
  const integrations = selectAllIntegrations(state);
  const topicSwitchSettings = selectTopicSwitchSettings(state);

  const roles = {
    instance: serviceInstanceId ? getInstanceRole(state) : null,
    assistant: assistant
      ? getAssistantRole(state, assistant.assistant_id)
      : null,
    skill: skill ? getSkillRole(state, skill.skill_id) : null
  };
  const plan = selectPlan(state);
  const metering = selectPlanMetering(state);
  const session = state.session;

  return {
    account,
    accountType,
    actionDetails,
    actionsAllActions,
    actionsAllSteps,
    actionsAutoSaveEnabled,
    actionsCurrentAction,
    actionsStepData,
    actionsTriggerExamples,
    assistant,
    // customExtensions,
    draftEnvironment,
    environments,
    integrations,
    isNewIa,
    metering,
    plan,
    region,
    roles,
    serviceInstanceId,
    session,
    skill,
    topicSwitchSettings
  };
};

export { SegmentTracker };
export default connect(mapStateToProps)(SegmentTracker as any);
